/**
 * tax/TaxApp.tsx — 세무사 사이트 「임지도사 협업방」 (roum-partners.com/tax · 2026-10-09 지도사 「세무사사무소용 사이트 — 아이콘 눌러서 들어가게」)
 *  첫 화면 = 기능 카드 3개(① 월간 소식지 만들기 ② 정책자금 대출 요청하기 ③ 정부지원금 리포트 요청하기)
 *  2026-10-09 디자인 시안(zip) 적용: 흐린 흰 머리줄(로고 「임」 + 이름 · 신청서는 오른쪽에 진행 「n / N 완료」 + 3px 진행 막대) · 「무엇을 도와드릴까요?」 카드
 *  주소: /tax · /tax/newsletter · /tax/loan · /tax/report(정부지원금 리포트 요청 · 2026-10-09) — 브라우저 뒤로 가기와 같이 움직인다
 *  UI/UX 자문(10인) 요약: 아이콘은 쓸 수 있는 것 위주(빈 약속 줄이기) · 준비 중은 같은 모양 + 배지 · 제공자와 데이터 처리 한 줄 · 이 달 자료 띠
 *  화면 문구에 「AI」 낱말 · 브라우저 alert/confirm 쓰지 않는다
 */
import React from 'react';
import { X, FONT, Glyph } from './taxUi';
import { getMonths, type TaxMonth } from './taxApi';
import NewsletterMaker from './NewsletterMaker';
import LoanRequest from './LoanRequest';
import ReportRequest from './ReportRequest';

type Route = 'home' | 'newsletter' | 'loan' | 'report';
const routeOf = (p: string): Route => (/^\/tax\/newsletter/.test(p) ? 'newsletter' : /^\/tax\/loan/.test(p) ? 'loan' : /^\/tax\/report/.test(p) ? 'report' : 'home');
const PATH: Record<Route, string> = { home: '/tax', newsletter: '/tax/newsletter', loan: '/tax/loan', report: '/tax/report' };

export interface Progress { label: string; pct: number }

export default function TaxApp() {
  const [route, setRoute] = React.useState<Route>(() => routeOf(window.location.pathname));
  const [months, setMonths] = React.useState<TaxMonth[] | null>(null);
  const [progress, setProgress] = React.useState<Progress | null>(null);
  React.useEffect(() => { const h = () => setRoute(routeOf(window.location.pathname)); window.addEventListener('popstate', h); return () => window.removeEventListener('popstate', h); }, []);
  React.useEffect(() => { getMonths().then(setMonths).catch(() => setMonths([])); }, []);
  React.useEffect(() => { document.title = route === 'newsletter' ? '월간 소식지 만들기 · 임지도사 협업방' : route === 'loan' ? '정책자금 대출 요청하기 · 임지도사 협업방' : route === 'report' ? '정부지원금 리포트 요청하기 · 임지도사 협업방' : '임지도사 협업방'; window.scrollTo(0, 0); }, [route]);
  const go = (r: Route) => { if (r !== route) { window.history.pushState(null, '', PATH[r]); setRoute(r); } };
  const inApp = /KAKAOTALK|NAVER\(inapp|Instagram|FBAN/i.test(navigator.userAgent || '');

  return (
    <div className="taxroot" style={{ minHeight: '100vh', background: X.bg, fontFamily: FONT, color: X.text, display: 'flex', flexDirection: 'column', wordBreak: 'keep-all', overflowWrap: 'anywhere', letterSpacing: '-0.01em', WebkitFontSmoothing: 'antialiased' }}>
      <style>{CSS}</style>
      {inApp && route === 'newsletter' && <div role="note" style={{ background: X.goldLt, color: X.gold, fontSize: 14, padding: '8px 16px', textAlign: 'center' }}>카카오톡 안에서는 PDF·JPG 저장이 막힐 수 있어요 — ⋮ 메뉴에서 「다른 브라우저로 열기」를 눌러 주세요</div>}
      {/* 진행 표시는 신청서에서만 — 자식 effect 가 부모 effect 보다 먼저 돌므로 route 바뀔 때 null 로 지우지 않고 여기서 거른다 */}
      <Header onHome={() => go('home')} progress={route === 'loan' || route === 'report' ? progress : null} />
      <main style={{ flex: 1 }}>
        {route === 'home' && <Home onGo={go} />}
        {route === 'loan' && <LoanRequest onHome={() => go('home')} onProgress={setProgress} />}
        {route === 'report' && <ReportRequest onHome={() => go('home')} onProgress={setProgress} />}
        {route === 'newsletter' && <NewsletterMaker months={months} onHome={() => go('home')} />}
      </main>
    </div>
  );
}

/** 입력칸 포커스 = 파란 테두리 + 흰 바탕(시안) · 카드 올라오기 · 키보드 초점 링 */
const CSS = [
  '@keyframes taxspin{to{transform:rotate(360deg)}}',
  '.taxroot input:not([type=checkbox]):not([type=file]):focus,.taxroot textarea:focus,.taxroot select:focus{border-color:#2F6BF0!important;background:#fff!important}',
  '.taxroot input::placeholder,.taxroot textarea::placeholder{color:#A9AEB6}',
  '.taxcard{transition:transform .15s,box-shadow .15s}.taxcard:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(23,25,28,.06)}',
  '.taxroot button:focus-visible,.taxroot a:focus-visible,.taxroot select:focus-visible{outline:3px solid #A8C3FA;outline-offset:2px}',
].join('');

/** 머리줄(시안) — 흐린 흰 바탕 · 「임」 네모 + 이름 · 신청서에서는 오른쪽 진행 글 + 아래 3px 진행 막대 */
function Header({ onHome, progress }: { onHome: () => void; progress: Progress | null }) {
  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 30, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: '1px solid #ECEEF1' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', height: 56, padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, boxSizing: 'border-box' }}>
        <button type="button" onClick={onHome} aria-label="처음 화면으로" style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
          <span aria-hidden style={{ width: 24, height: 24, borderRadius: 7, background: X.blue, color: '#fff', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>임</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: X.text }}>임지도사 협업방</span>
        </button>
        {progress && <span style={{ fontSize: 13, color: X.sub }}>{progress.label}</span>}
      </div>
      {progress && <div style={{ height: 3, background: '#ECEEF1' }}><div style={{ height: 3, background: X.blue, width: `${progress.pct}%`, transition: 'width .35s ease' }} /></div>}
    </header>
  );
}

interface Card { id: Route; title: string; desc: string; glyph: 'letter' | 'loan' | 'report'; color: string; bg: string }
/** 기능 카드(시안 문구 그대로) */
const CARDS: Card[] = [
  { id: 'newsletter', title: '월간 소식지 만들기', desc: '고객에게 보낼 지원사업·세무일정 소식지를 A4 한 장으로 만들어요.', glyph: 'letter', color: '#2F6BF0', bg: '#E8F0FE' },
  { id: 'loan', title: '정책자금 대출 요청하기', desc: '중진공, 소진공, 신보, 기보, 재단 등 협업 지점을 통해 빠르게 알아봐드려요.', glyph: 'loan', color: '#14905E', bg: '#E6F6EF' },
  { id: 'report', title: '정부지원금 리포트 요청하기', desc: '고객사가 받을 수 있는 정부지원금을 정리한 리포트를 요청해요.', glyph: 'report', color: '#7C3AED', bg: '#F1ECFE' },
];

function Home({ onGo }: { onGo: (r: Route) => void }) {
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 20px 80px', display: 'flex', flexDirection: 'column', gap: 28, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 4px' }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em' }}>무엇을 도와드릴까요?</h1>
        <p style={{ margin: 0, fontSize: 15, color: X.sub }}>고객사 업무에 필요한 도구를 골라 바로 시작하세요.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
        {CARDS.map((c) => (
          <button key={c.id} type="button" className="taxcard" onClick={() => onGo(c.id)}
            style={{ textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer', border: 'none', background: X.card, borderRadius: 20, padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <span style={{ width: 52, height: 52, borderRadius: 14, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Glyph name={c.glyph} size={26} color={c.color} stroke={2} />
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: X.text }}>{c.title}</span>
              <span style={{ fontSize: 14, color: X.sub, lineHeight: 1.55 }}>{c.desc}</span>
            </span>
            <span style={{ fontSize: 14, fontWeight: 600, color: X.blue }}>시작하기 →</span>
          </button>
        ))}
      </div>
    </div>
  );
}
